import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, onReachability } from '../../api/client'
import { useUser } from '../../auth/AuthContext'
import { DataTable } from '../../components/DataTable'
import { Icon } from '../../components/Icon'
import { Card, ErrorState, PageHeader, StatusBadge } from '../../components/ui'
import { formatDateTime } from '../../lib/format'
import { useOnline } from '../../lib/hooks'
import { DRAFTS_CHANGED, deleteDraft, listDrafts, saveDraft, summarizeDraft, type InspectionDraft } from './draftStore'
import { syncDraft } from './sync'

/** A draft is worth showing when a submit failed, or it has at least one answer. */
export function isPending(d: InspectionDraft): boolean {
  if (d.status === 'synced') return false
  return d.status === 'pending_sync' || summarizeDraft(d).answered > 0
}

export type SyncFn = (draft: InspectionDraft) => Promise<unknown>

export const defaultSync: SyncFn = (draft) => syncDraft(draft, { api, save: saveDraft })

export function usePendingDrafts(userId: number): InspectionDraft[] {
  const [drafts, setDrafts] = useState<InspectionDraft[]>([])
  useEffect(() => {
    let active = true
    const refresh = () => {
      void listDrafts(userId).then((all) => {
        // IndexedDB reads can finish after unmount or a change of user.
        if (active) setDrafts(all.filter(isPending))
      })
    }
    refresh()
    window.addEventListener(DRAFTS_CHANGED, refresh)
    return () => {
      active = false
      window.removeEventListener(DRAFTS_CHANGED, refresh)
    }
  }, [userId])
  return drafts
}

/** When the connection returns, submit the drafts whose submit had failed (never the ones still being filled in). */
export function useAutoSync(userId: number, sync: SyncFn = defaultSync): void {
  const running = useRef(false)
  useEffect(() => {
    const run = async () => {
      if (running.current) return
      running.current = true
      try {
        for (const d of await listDrafts(userId)) {
          if (d.status !== 'pending_sync') continue
          try {
            await sync(d)
            await deleteDraft(userId, d.equipmentCode)
          } catch {
            /* stays on the device, shown in the drafts list */
          }
        }
      } finally { running.current = false }
    }
    window.addEventListener('online', run)
    let down = false
    const stop = onReachability((online) => { if (!online) down = true; else if (down) { down = false; void run() } })
    return () => { window.removeEventListener('online', run); stop() }
  }, [userId, sync])
}

export function OfflineIndicator() {
  const user = useUser()
  const online = useOnline()
  const pending = usePendingDrafts(user.id)
  if (online && pending.length === 0) return null
  const n = pending.length
  return (
    <div role="status" className="offline-bar" data-offline={!online}>
      <Icon name={online ? 'save' : 'wifi-off'} size={16} />
      <span>
        {online ? '' : 'Sin conexión. '}
        {n > 0 ? `${n} ${n === 1 ? 'borrador' : 'borradores'} ${online ? 'esperando' : n === 1 ? 'guardado' : 'guardados'} en este dispositivo.` : 'Los borradores se quedan en este dispositivo hasta que se envían.'}
      </span>
      {n > 0 ? <Link to="/drafts">Revisar borradores</Link> : null}
    </div>
  )
}

export function PendingDraftsPage({ sync = defaultSync }: { sync?: SyncFn }) {
  const user = useUser()
  const drafts = usePendingDrafts(user.id)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  async function run(d: InspectionDraft) {
    setBusy(d.key); setError(null)
    try { await sync(d); await deleteDraft(user.id, d.equipmentCode) } catch (e) { setError(e) } finally { setBusy(null) }
  }
  return (
    <div className="page page--narrow">
      <PageHeader title="Borradores en este dispositivo" subtitle="Inspecciones guardadas localmente. Se envían al servidor solo cuando se envían de forma explícita" />
      {error ? <ErrorState error={error} /> : null}
      <Card>
        <DataTable caption="Borradores" rows={drafts} rowKey={(d) => d.key} emptyTitle="No hay borradores en este dispositivo" columns={[
          { key: 'c', header: 'Equipo', cell: (d) => <span className="mono">{d.equipmentCode}</span> },
          { key: 's', header: 'Estado', cell: (d) => (d.status === 'pending_sync' ? <StatusBadge tone="warn" label="Esperando sincronización" icon="wifi-off" /> : <StatusBadge tone="neutral" label="En curso" />) },
          { key: 'a', header: 'Respondidos', cell: (d) => `${summarizeDraft(d).answered} criterios, ${summarizeDraft(d).photos} fotos` },
          { key: 'u', header: 'Guardado', cell: (d) => formatDateTime(new Date(d.updatedAt).toISOString()), priority: 'low' },
          { key: 'x', header: '', cell: (d) => (
            <span className="row">
              {d.status === 'pending_sync' ? <button type="button" className="btn btn--primary btn--sm" disabled={busy === d.key} onClick={() => void run(d)}>Sincronizar ahora</button> : <Link className="btn btn--primary btn--sm" to={`/e/${d.equipmentCode}/inspect`}>Continuar</Link>}
              {confirm === d.key
                ? <button type="button" className="btn btn--danger btn--sm" onClick={() => void deleteDraft(user.id, d.equipmentCode).then(() => setConfirm(null))}>Confirmar descarte</button>
                : <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirm(d.key)}>Descartar</button>}
            </span>) },
        ]} />
      </Card>
    </div>
  )
}
